const VISITOR_KEY = "bt_analytics_visitor_id";
const SESSION_KEY = "bt_analytics_session";
const CONSENT_KEY = "bt_analytics_consent";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const BOOKING_IDLE_MS = 30 * 60 * 1000;
const MAX_BATCH_SIZE = 50;

let currentUserId = null;
let queue = [];
let flushTimer = null;
let pageContext = { path: "", lastStep: null, artistId: null };
let exitSent = false;
// Booking journey context (artist, city, category, event type) added to every
// booking/payment event so reports can filter whole journeys.
let journey = {};
// Set once the visitor hands off to the payment gateway or completes the
// booking: leaving the page after that is not an exit.
let bookingHandedOff = false;
let idleTimer = null;

const safeStorage = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const createId = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch {
    // Fall through to the local identifier.
  }
  return `bt-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const isTrackingAllowed = () => {
  try {
    if (navigator.doNotTrack === "1" || window.doNotTrack === "1") return false;
    const consent = safeStorage()?.getItem(CONSENT_KEY);
    return consent !== "denied" && consent !== "false";
  } catch {
    return false;
  }
};

export const setTrackingConsent = (allowed) => {
  try {
    safeStorage()?.setItem(CONSENT_KEY, allowed ? "granted" : "denied");
  } catch {
    // Tracking must never affect the application.
  }
};

const visitorId = () => {
  const storage = safeStorage();
  if (!storage) return createId();
  try {
    let value = storage.getItem(VISITOR_KEY);
    if (!value) {
      value = createId();
      storage.setItem(VISITOR_KEY, value);
    }
    return value;
  } catch {
    return createId();
  }
};

const sessionId = () => {
  const storage = safeStorage();
  const now = Date.now();
  if (!storage) return createId();
  try {
    const raw = storage.getItem(SESSION_KEY);
    const previous = raw ? JSON.parse(raw) : null;
    if (previous?.id && now - Number(previous.lastActivity || 0) <= SESSION_TIMEOUT_MS) {
      storage.setItem(SESSION_KEY, JSON.stringify({ id: previous.id, lastActivity: now }));
      return previous.id;
    }
    const id = createId();
    storage.setItem(SESSION_KEY, JSON.stringify({ id, lastActivity: now }));
    return id;
  } catch {
    return createId();
  }
};

const deviceType = () => {
  const width = window.innerWidth || 1024;
  return width < 768 ? "mobile" : width < 1100 ? "tablet" : "desktop";
};

const browser = () => {
  const ua = navigator.userAgent || "";
  if (/Edg\//i.test(ua)) return "edge";
  if (/Chrome\//i.test(ua)) return "chrome";
  if (/Firefox\//i.test(ua)) return "firefox";
  if (/Safari\//i.test(ua)) return "safari";
  return "other";
};

const utm = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
  };
};

// Never send query strings (they can hold reset tokens, emails…) except utm_*.
const safePagePath = () => {
  const params = new URLSearchParams(window.location.search);
  const kept = [...params.keys()].filter((key) => key.startsWith("utm_"));
  const query = kept.map((key) => `${key}=${encodeURIComponent(params.get(key))}`).join("&");
  return window.location.pathname + (query ? `?${query}` : "");
};

// Only the referring site, not the full URL.
const referrerOrigin = () => {
  try {
    return document.referrer ? new URL(document.referrer).origin : "";
  } catch {
    return "";
  }
};

const isJourneyEvent = (eventName) => eventName.startsWith("booking_") || eventName.startsWith("payment_");

const baseEvent = (eventName, data) => ({
  event_name: eventName,
  ts: new Date().toISOString(),
  visitor_id: visitorId(),
  session_id: sessionId(),
  ...(currentUserId ? { user_id: currentUserId } : {}),
  page_path: safePagePath(),
  device_type: deviceType(),
  browser: browser(),
  referrer: referrerOrigin(),
  ...utm(),
  ...(isJourneyEvent(eventName) ? journey : {}),
  ...Object.fromEntries(Object.entries(data).filter(([, value]) => value !== undefined && value !== null && value !== "")),
});

const send = (events, useBeacon = false) => {
  if (!events.length) return;
  try {
    const body = JSON.stringify({ events });
    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon("/api/analytics/events", blob)) return;
    }
    fetch("/api/analytics/events", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: useBeacon,
    }).catch(() => {});
  } catch {
    // Analytics is strictly best effort.
  }
};

export const flush = (useBeacon = false) => {
  try {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    while (queue.length) send(queue.splice(0, MAX_BATCH_SIZE), useBeacon);
  } catch {
    queue = [];
  }
};

export const setTrackingUser = (user) => {
  currentUserId = user?.id || null;
};

const onBookingPage = (path) => String(path || "").startsWith("/book/");

const clearIdleTimer = () => {
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
};

const armIdleTimer = () => {
  clearIdleTimer();
  if (!onBookingPage(pageContext.path) || !pageContext.lastStep || bookingHandedOff) return;
  idleTimer = window.setTimeout(() => trackBookingExit("idle_timeout"), BOOKING_IDLE_MS);
};

export const setTrackingPage = (context = {}) => {
  try {
    // Leaving the booking page inside the app is an exit.
    if (context.path && onBookingPage(pageContext.path) && !onBookingPage(context.path)) {
      trackBookingExit("navigation_away");
      pageContext = { path: context.path, lastStep: null, artistId: null };
      journey = {};
      clearIdleTimer();
      return;
    }
    if (context.path && onBookingPage(context.path) && !onBookingPage(pageContext.path)) {
      bookingHandedOff = false;
      exitSent = false;
    }
    if (context.journey) journey = { ...journey, ...context.journey };
    const { journey: _ignored, ...rest } = context;
    pageContext = { ...pageContext, ...rest };
    armIdleTimer();
  } catch {
    // Tracking must never affect the application.
  }
};

// Call when the visitor hands off to the payment gateway or completes the
// booking, so leaving the page afterwards is not recorded as an exit.
export const markBookingHandoff = () => {
  bookingHandedOff = true;
  clearIdleTimer();
};

// Where the visitor clicked an artist from (home section, search, planner,
// gallery…). Kept briefly so the profile view — possibly in a new tab — can
// report it; document.referrer does not change on in-app navigation.
const CLICK_SOURCE_KEY = "bt_analytics_artist_source";
const rememberClickSource = (artistId, source) => {
  try {
    safeStorage()?.setItem(CLICK_SOURCE_KEY, JSON.stringify({ artistId, source, at: Date.now() }));
  } catch {
    // ignore
  }
};
export const consumeArtistClickSource = (artistId) => {
  try {
    const storage = safeStorage();
    const saved = JSON.parse(storage?.getItem(CLICK_SOURCE_KEY) || "null");
    storage?.removeItem(CLICK_SOURCE_KEY);
    if (saved && Date.now() - saved.at < 2 * 60 * 1000 && (!artistId || !saved.artistId || saved.artistId === artistId)) {
      return saved.source;
    }
  } catch {
    // ignore
  }
  return null;
};

export const track = (eventName, data = {}) => {
  if (!isTrackingAllowed()) return;
  try {
    if (eventName === "artist_card_click") rememberClickSource(data.artist_id, data.source || "listing");
    if (eventName === "planner_suggestion_click") rememberClickSource(data.artist_id, "planner");
    const event = baseEvent(eventName, data);
    queue.push(event);
    if (queue.length >= MAX_BATCH_SIZE) flush();
    else if (!flushTimer) flushTimer = window.setTimeout(() => flush(), 1000);
  } catch {
    // Never allow tracking to interrupt a user action.
  }
};

export const trackBookingExit = (reason = "navigation_away") => {
  if (!isTrackingAllowed() || exitSent || !pageContext.lastStep || bookingHandedOff) return;
  exitSent = true;
  clearIdleTimer();
  track("booking_exit", {
    last_step: pageContext.lastStep,
    time_on_page: pageContext.bookingStartedAt ? Date.now() - pageContext.bookingStartedAt : null,
    reason,
  });
  flush(true);
};

if (typeof window !== "undefined") {
  // Closing/leaving the tab is an exit; merely switching tabs is not — just send what is queued.
  window.addEventListener("pagehide", () => {
    try {
      if (onBookingPage(pageContext.path)) trackBookingExit("tab_close");
      flush(true);
    } catch {
      // ignore
    }
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });
  // Activity on the booking page restarts the idle-exit timer.
  ["pointerdown", "keydown", "scroll"].forEach((type) => {
    window.addEventListener(type, () => { if (idleTimer) armIdleTimer(); }, { passive: true });
  });
}

export const getTrackingConsent = () => {
  try {
    return safeStorage()?.getItem(CONSENT_KEY) || "granted";
  } catch {
    return "denied";
  }
};
